import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { Card, LinkButton, Tag } from '@onefootprint/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardError, CardSkeleton } from '@/components/entity-onboarding-details';
import { isForbiddenError } from '@/components/forbidden';
import StatusBadge from '@/components/status-badge';
import useCurrentEntityDuplicateData from '@/entity/hooks/use-current-entity-duplicate-data';
import useSession from '@/hooks/use-session';
import getTruncatedName from '@/utils/get-truncated-name';
import useDupeKindLabels from './hooks/use-dupe-kind-labels';
const COLLAPSED_ROW_COUNT = 10;
// Collapsing only pays off when it hides a few rows.
const MIN_HIDDEN_ROW_COUNT = 3;

// Four columns once there's room, shared by the header and the rows so they line up. Narrower, each row
// stacks: the account and its status, when it was created, then what it matched on.
const GRID = 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 @2xl:grid-cols-[minmax(13.5rem,1.4fr)_minmax(0,1.3fr)_6rem_9.5rem]';
type DuplicateDataProps = {
  /** Shows the duplicates as they were at this time instead of now. */
  timestamp?: string;
};
const DuplicateData = t0 => {
  const $ = _c(63);
  const {
    timestamp
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "duplicateData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const router = useRouter();
  const {
    formatDateWithTime
  } = useIntl();
  const {
    data: t2
  } = useSession();
  const {
    org
  } = t2;
  const {
    data: duplicateData,
    error
  } = useCurrentEntityDuplicateData(timestamp);
  const dupeKindToTranslation = useDupeKindLabels();
  const [isExpanded, setIsExpanded] = useState(false);
  let T0;
  let T1;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[1] !== dupeKindToTranslation || $[2] !== duplicateData || $[3] !== error || $[4] !== formatDateWithTime || $[5] !== isExpanded || $[6] !== org?.name || $[7] !== router.asPath || $[8] !== t) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const dupes = duplicateData?.data ?? [];
      const isCollapsible = dupes.length - COLLAPSED_ROW_COUNT >= MIN_HIDDEN_ROW_COUNT;
      let t8;
      if ($[16] !== router.asPath) {
        t8 = router.asPath.split("/");
        $[16] = router.asPath;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      const [, base] = t8;
      if (!duplicateData) {
        if (!error) {
          let t9;
          if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
            t9 = <CardSkeleton rows={3} />;
            $[18] = t9;
          } else {
            t9 = $[18];
          }
          t7 = t9;
          break bb0;
        }
        let t9;
        if ($[19] !== error || $[20] !== t) {
          t9 = isForbiddenError(error) ? <Card.Root><Card.Empty>{t(_temp)}</Card.Empty></Card.Root> : <CardError error={error} />;
          $[19] = error;
          $[20] = t;
          $[21] = t9;
        } else {
          t9 = $[21];
        }
        t7 = t9;
        break bb0;
      }
      if (!dupes.length) {
        const t9 = org?.name ?? "";
        let t10;
        if ($[22] !== t || $[23] !== t9) {
          t10 = t(_temp2, {
            orgName: t9
          });
          $[22] = t;
          $[23] = t9;
          $[24] = t10;
        } else {
          t10 = $[24];
        }
        let t11;
        if ($[25] !== t10) {
          t11 = <Card.Root><Card.Empty>{t10}</Card.Empty></Card.Root>;
          $[25] = t10;
          $[26] = t11;
        } else {
          t11 = $[26];
        }
        t7 = t11;
        break bb0;
      }
      T1 = Card.Root;
      let t9;
      if ($[27] !== t) {
        t9 = t(_temp3);
        $[27] = t;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      let t10;
      if ($[29] !== t9) {
        t10 = <span>{t9}</span>;
        $[29] = t9;
        $[30] = t10;
      } else {
        t10 = $[30];
      }
      let t11;
      if ($[31] !== t) {
        t11 = t(_temp4);
        $[31] = t;
        $[32] = t11;
      } else {
        t11 = $[32];
      }
      let t12;
      if ($[33] !== t11) {
        t12 = <span>{t11}</span>;
        $[33] = t11;
        $[34] = t12;
      } else {
        t12 = $[34];
      }
      let t13;
      if ($[35] !== t) {
        t13 = t(_temp5);
        $[35] = t;
        $[36] = t13;
      } else {
        t13 = $[36];
      }
      let t14;
      if ($[37] !== t13) {
        t14 = <span>{t13}</span>;
        $[37] = t13;
        $[38] = t14;
      } else {
        t14 = $[38];
      }
      let t15;
      if ($[39] !== t) {
        t15 = t(_temp6);
        $[39] = t;
        $[40] = t15;
      } else {
        t15 = $[40];
      }
      let t16;
      if ($[41] !== t15) {
        t16 = <span>{t15}</span>;
        $[41] = t15;
        $[42] = t16;
      } else {
        t16 = $[42];
      }
      if ($[43] !== t10 || $[44] !== t12 || $[45] !== t14 || $[46] !== t16) {
        t6 = <div aria-hidden={true} className={`${GRID} mb-1 hidden h-6 items-center px-[calc(0.75rem+0.3125rem)] text-label-3 text-tertiary @2xl:grid`}>{t10}{t12}{t14}{t16}</div>;
        $[43] = t10;
        $[44] = t12;
        $[45] = t14;
        $[46] = t16;
        $[47] = t6;
      } else {
        t6 = $[47];
      }
      T0 = Card.Body;
      t3 = "p-1";
      let t17;
      if ($[48] !== t) {
        t17 = t(_temp7);
        $[48] = t;
        $[49] = t17;
      } else {
        t17 = $[49];
      }
      let t18;
      if ($[50] !== base || $[51] !== dupeKindToTranslation || $[52] !== formatDateWithTime) {
        t18 = dupe => {
          const createdAt = formatDateWithTime(new Date(dupe.startTimestamp), {
            month: "short",
            year: "numeric"
          });
          return <li key={dupe.fpId}><Link href={`/${base}/${dupe.fpId}`} target="_blank" rel="noopener noreferrer" className={`${GRID} items-center rounded-default px-3 pt-2 pb-2.5 no-underline transition-colors duration-100 hover:bg-hover`}><span className="col-start-1 row-start-1 flex min-w-0 flex-col"><span className="truncate text-label-2 text-primary">{getTruncatedName(dupe.data)}</span><span className="truncate text-snippet-3 text-tertiary">{dupe.fpId}</span></span><span className="col-span-2 row-start-3 flex min-w-0 flex-wrap gap-1 @2xl:col-span-1 @2xl:col-start-2 @2xl:row-start-1">{dupe.dupeKinds.map(kind => <Tag key={kind} className="max-w-full" title={dupeKindToTranslation[kind]}><span className="truncate">{dupeKindToTranslation[kind]}</span></Tag>)}</span><span className="col-start-2 row-start-1 justify-self-end @2xl:col-start-3 @2xl:justify-self-start"><StatusBadge status={dupe.status} /></span><span className="col-start-1 row-start-2 -mt-1.5 truncate text-body-3 text-tertiary @2xl:col-start-4 @2xl:row-start-1 @2xl:mt-0">{createdAt}</span></Link></li>;
        };
        $[50] = base;
        $[51] = dupeKindToTranslation;
        $[52] = formatDateWithTime;
        $[53] = t18;
      } else {
        t18 = $[53];
      }
      t4 = <ul aria-label={t17} className="flex flex-col gap-0.5">{(isExpanded || !isCollapsible ? dupes : dupes.slice(0, COLLAPSED_ROW_COUNT)).map(t18)}</ul>;
      t5 = isCollapsible && <LinkButton className="mx-3 my-2" onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? t(_temp8) : t(_temp9, {
          count: dupes.length
        })}</LinkButton>;
    }
    $[1] = dupeKindToTranslation;
    $[2] = duplicateData;
    $[3] = error;
    $[4] = formatDateWithTime;
    $[5] = isExpanded;
    $[6] = org?.name;
    $[7] = router.asPath;
    $[8] = t;
    $[9] = T0;
    $[10] = T1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    T0 = $[9];
    T1 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[54] !== T0 || $[55] !== t3 || $[56] !== t4 || $[57] !== t5) {
    t8 = <T0 className={t3}>{t4}{t5}</T0>;
    $[54] = T0;
    $[55] = t3;
    $[56] = t4;
    $[57] = t5;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== T1 || $[60] !== t6 || $[61] !== t8) {
    t9 = <T1>{t6}{t8}</T1>;
    $[59] = T1;
    $[60] = t6;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  return t9;
};
export default DuplicateData;
function _temp($) {
  return $.table.noPermission;
}
function _temp2($_0) {
  return $_0.table.emptyState;
}
function _temp3($_1) {
  return $_1.table.header.name;
}
function _temp4($_2) {
  return $_2.table.header.exactMatch;
}
function _temp5($_3) {
  return $_3.table.header.status;
}
function _temp6($_4) {
  return $_4.table.header.createdAt;
}
function _temp7($_5) {
  return $_5.table.ariaLabel;
}
function _temp8($_6) {
  return $_6.showLess;
}
function _temp9($_7) {
  return $_7.showAll;
}